/* eslint-disable max-lines */
import { IconInfoCircle } from '@tabler/icons-react';
import { Gender, InsuredSubtype, PossibleFipsCode } from '@zorro/clients';
import {
  VALIDATION_MESSAGES,
  insuredMaxDateOfBirth,
  insuredMinDateOfBirth,
} from '@zorro/common-utils';
import { Checkbox } from '@zorro/design-system/v2/Checkbox';
import { Divider } from '@zorro/design-system/v2/Divider';
import { RadioGroup } from '@zorro/design-system/v2/Radio';
import { Select } from '@zorro/design-system/v2/Select';
import { SsnPasswordInput } from '@zorro/design-system/v2/SsnPasswordInput';
import { TextInput } from '@zorro/design-system/v2/TextInput';
import { Tooltip } from '@zorro/design-system/v2/Tooltip';
import { DateUtilInstance, getNow } from '@zorro/shared/formatters';
import { insuredSubtypeLabelConfig } from '@zorro/types';
import {
  AddressInput,
  Box,
  DateInput,
  FormErrorMessage,
  Grid,
  Group,
  Icon,
  Text,
} from '@zorro/web/design-system';
import { Fragment } from 'react';
import { Controller, UseFormReturn } from 'react-hook-form';
import * as yup from 'yup';

import { CitizenshipStatusInput } from '../CitizenshipStatusInput';
import { AnticipatedCareLevelInput } from './AnticipatedCareLevelInput';
import {
  InsuredFormGenderRadios,
  InsuredFormYesNoRadios,
} from './InsuredFormRadios';
import {
  INSURED_FORM_ADDRESS_TOOLTIP_LABEL,
  getInsuredBaseSchema,
} from './InsuredFormUtils';

const dependentRelationshipValues = Object.values([
  InsuredSubtype.CHILD,
  InsuredSubtype.OTHER_DEPENDENT,
]);

const insuredTypeOptions = dependentRelationshipValues.map((status) => ({
  label: insuredSubtypeLabelConfig[status],
  value: status,
}));

export const getDependentSchema = (
  isFinalizationMode: boolean,
  withAnticipatedCareLevel: boolean,
  minDateOfBirth: Date = getNow().subtract(26, 'years').toDate(),
  shouldValidateDateOfBirth = false
) =>
  getInsuredBaseSchema(
    isFinalizationMode,
    false,
    withAnticipatedCareLevel
  ).concat(
    yup.object({
      insuredId: yup.string().optional(),
      fipsCode: yup.string().optional(),
      isDisabled: yup.boolean().optional().default(false),
      dateOfBirth: yup
        .date()
        .typeError(VALIDATION_MESSAGES.dateOfBirthRequired)
        .required(VALIDATION_MESSAGES.dateOfBirthRequired)
        .when('isDisabled', {
          is: (isDisabled?: boolean) =>
            shouldValidateDateOfBirth && !isDisabled,
          then: (schema) =>
            schema.min(
              minDateOfBirth,
              VALIDATION_MESSAGES.dependentMaxAgeValid
            ),
        }),
      ...(isFinalizationMode
        ? {
            subtype: yup
              .mixed<InsuredSubtype>()
              .required(VALIDATION_MESSAGES.relationshipRequired)
              .oneOf(
                dependentRelationshipValues,
                VALIDATION_MESSAGES.relationshipRequired
              ),
          }
        : {
            subtype: yup
              .mixed<InsuredSubtype>()
              .oneOf(
                dependentRelationshipValues,
                VALIDATION_MESSAGES.relationshipRequired
              )
              .nullable(),
          }),
    })
  );

export type DependentSchema = ReturnType<typeof getDependentSchema>;

export const getDependentsFormSchema = (dependentSchema: DependentSchema) =>
  yup.object({
    dependents: yup.array().of(dependentSchema).required(),
  });

export const getDependentInsuredSchema = (
  isFinalizationMode: boolean,
  shouldValidateDateOfBirth: boolean,
  withAnticipatedCareLevel: boolean,
  minDateOfBirth?: Date
) =>
  getDependentsFormSchema(
    getDependentSchema(
      isFinalizationMode,
      withAnticipatedCareLevel,
      minDateOfBirth,
      shouldValidateDateOfBirth
    )
  );

export type InsuredDependentFormFields = yup.InferType<DependentSchema>;
export type InsuredDependentsFormFields = yup.InferType<
  ReturnType<typeof getDependentInsuredSchema>
>;

type DependentFormProps = {
  index: number;
  dependentsForm: UseFormReturn<InsuredDependentsFormFields>;
  targetEnrollmentDate?: DateUtilInstance;
  isFinalizationMode: boolean;
  isEmployeeMode: boolean;
  possibleFipsCodes: PossibleFipsCode[];
  setPossibleFipsCodes: (codes: PossibleFipsCode[]) => void;
  withAnticipatedCareLevel?: boolean;
  withResidentialAddress?: boolean;
};

export const DependentForm = ({
  index,
  dependentsForm,
  targetEnrollmentDate,
  isFinalizationMode,
  isEmployeeMode,
  possibleFipsCodes,
  setPossibleFipsCodes,
  withAnticipatedCareLevel,
  withResidentialAddress = true,
}: DependentFormProps) => {
  'use no memo';
  const { control, formState, watch, setValue, trigger } = dependentsForm;

  const dependent = watch(`dependents.${index}`);

  const isFemale = dependent?.gender === Gender.FEMALE;

  return (
    <Grid>
      <Grid.Col span={{ sm: 6 }}>
        <Controller
          name={`dependents.${index}.firstName`}
          control={control}
          render={({ field: { ...rest } }) => (
            <TextInput
              {...rest}
              label="First name"
              placeholder="First name"
              required
              isMasked
              size="lg"
            />
          )}
        />

        <FormErrorMessage
          errors={formState.errors}
          fieldName={`dependents.${index}.firstName`}
        />
      </Grid.Col>

      <Grid.Col span={{ sm: 6 }}>
        <Controller
          name={`dependents.${index}.lastName`}
          control={control}
          render={({ field: { ...rest } }) => (
            <TextInput
              {...rest}
              label="Last name"
              placeholder="Last name"
              required
              isMasked
              size="lg"
            />
          )}
        />

        <FormErrorMessage
          errors={formState.errors}
          fieldName={`dependents.${index}.lastName`}
        />
      </Grid.Col>

      <Grid.Col span={{ sm: 6 }}>
        <Controller
          name={`dependents.${index}.subtype`}
          control={control}
          render={({ field: { ...rest } }) => (
            <Select
              {...rest}
              label="Relationship"
              placeholder="Select relationship"
              data={insuredTypeOptions}
              required={isFinalizationMode}
              size="lg"
            />
          )}
        />

        <FormErrorMessage
          errors={formState.errors}
          fieldName={`dependents.${index}.subtype`}
        />
      </Grid.Col>

      <Grid.Col span={{ sm: 6 }}>
        <Controller
          name={`dependents.${index}.dateOfBirth`}
          control={control}
          render={({ field }) => (
            <DateInput
              {...field}
              label="Date of birth"
              defaultLevel="decade"
              popoverProps={{ withinPortal: false }}
              minDate={
                dependent?.isDisabled
                  ? undefined
                  : insuredMinDateOfBirth(true, targetEnrollmentDate)
              }
              maxDate={insuredMaxDateOfBirth(true, targetEnrollmentDate)}
              withCalendarIcon={false}
              styles={{
                input: { height: '3rem', minHeight: '3rem' },
                label: { marginBottom: '0.5rem' },
              }}
              size="lg"
              required
            />
          )}
        />

        <FormErrorMessage
          errors={formState.errors}
          fieldName={`dependents.${index}.dateOfBirth`}
        />

        <Controller
          control={control}
          name={`dependents.${index}.isDisabled`}
          render={({ field: { value, onChange, ...rest } }) => (
            <Group
              gap="var(--space-6)"
              wrap="nowrap"
              align="center"
              mt="var(--space-12)"
              mih="var(--font-line-height-22)"
            >
              <Checkbox
                {...rest}
                checked={Boolean(value)}
                onChange={(event) => {
                  onChange(event.currentTarget.checked);
                  void trigger(`dependents.${index}.dateOfBirth`);
                }}
                label={
                  <Text size="sm" textWrap="nowrap">
                    Adult child with a disability (no age limit)
                  </Text>
                }
              />
              <Tooltip
                label="For an adult child who can't support themselves due to a disability. They may be eligible to stay covered past the usual age limit. Additional documentation may be required"
                position="top"
              >
                <Box display="flex" style={{ alignItems: 'center' }}>
                  <Icon icon={IconInfoCircle} size="1rem" />
                </Box>
              </Tooltip>
            </Group>
          )}
        />
      </Grid.Col>

      <Grid.Col span={{ sm: 6 }}>
        <Controller
          control={control}
          name={`dependents.${index}.gender`}
          render={({ field }) => (
            <RadioGroup {...field} label="Gender" required={isFinalizationMode}>
              <InsuredFormGenderRadios />
            </RadioGroup>
          )}
        />

        <FormErrorMessage
          errors={formState.errors}
          fieldName={`dependents.${index}.gender`}
        />
      </Grid.Col>

      <Grid.Col span={{ sm: 6 }}>
        <Divider hiddenFrom="sm" mt="var(--space-8)" mb="var(--space-16)" />

        <Controller
          control={control}
          name={`dependents.${index}.isSmoker`}
          render={({ field }) => (
            <RadioGroup
              {...field}
              label="Tobacco user"
              required={isFinalizationMode}
            >
              <InsuredFormYesNoRadios />
            </RadioGroup>
          )}
        />

        <FormErrorMessage
          errors={formState.errors}
          fieldName={`dependents.${index}.isSmoker`}
        />
      </Grid.Col>

      {isFemale && (
        <>
          <Grid.Col span={{ sm: 6 }}>
            <Divider hiddenFrom="sm" mt="var(--space-8)" mb="var(--space-16)" />

            <Controller
              control={control}
              name={`dependents.${index}.isPregnant`}
              render={({ field }) => (
                <RadioGroup
                  {...field}
                  label="Pregnant"
                  required={isFinalizationMode}
                >
                  <InsuredFormYesNoRadios />
                </RadioGroup>
              )}
            />

            <FormErrorMessage
              errors={formState.errors}
              fieldName={`dependents.${index}.isPregnant`}
            />
          </Grid.Col>
          <Grid.Col span={{ sm: 6 }} />
        </>
      )}

      <Grid.Col span={{ sm: 6 }}>
        <Controller
          name={`dependents.${index}.ssn`}
          control={control}
          render={({ field: { value, ...rest } }) => (
            <SsnPasswordInput
              {...rest}
              label="SSN"
              value={value || ''}
              required={isFinalizationMode}
              size="lg"
            />
          )}
        />

        <FormErrorMessage
          errors={formState.errors}
          fieldName={`dependents.${index}.ssn`}
        />
      </Grid.Col>

      <Grid.Col span={{ sm: 6 }}>
        <Controller
          name={`dependents.${index}.citizenshipStatus`}
          control={control}
          render={({ field }) => (
            <CitizenshipStatusInput
              {...field}
              selectProps={{
                required: isFinalizationMode,
                clearable: !isFinalizationMode,
              }}
            />
          )}
        />

        <FormErrorMessage
          errors={formState.errors}
          fieldName={`dependents.${index}.citizenshipStatus`}
        />
      </Grid.Col>

      {withResidentialAddress && (
        <>
          <Grid.Col>
            <Controller
              control={control}
              name={`dependents.${index}.residentialAddress`}
              render={({ field: { value, onBlur, onChange, ...rest } }) => (
                <AddressInput
                  {...rest}
                  onBlur={() => {
                    onChange(value);
                    onBlur();
                    setValue(`dependents.${index}.fipsCode`, undefined);
                    setPossibleFipsCodes([]);
                  }}
                  notifySubscribers={(newAddress) => onChange(newAddress)}
                  onChange={onChange}
                  label={
                    <Tooltip
                      label={INSURED_FORM_ADDRESS_TOOLTIP_LABEL}
                      disabled={!isEmployeeMode}
                      position="top"
                      maw={400}
                    >
                      <span>Residential address</span>
                    </Tooltip>
                  }
                  value={value || ''}
                  placeholder="Type residential address"
                  required={isFinalizationMode}
                  disabled={isEmployeeMode}
                  isMasked
                />
              )}
            />

            <FormErrorMessage
              errors={formState.errors}
              fieldName={`dependents.${index}.residentialAddress`}
            />
          </Grid.Col>
          {possibleFipsCodes.length > 1 && (
            <Grid.Col>
              <Controller
                control={control}
                name={`dependents.${index}.fipsCode`}
                render={({ field: { ...rest } }) => (
                  <Select
                    {...rest}
                    label="County of residence"
                    placeholder="Select county of residence"
                    data={possibleFipsCodes.map(({ fipsCode, county }) => ({
                      label: county,
                      value: fipsCode,
                    }))}
                  />
                )}
              />
              <FormErrorMessage
                errors={formState.errors}
                fieldName={`dependents.${index}.fipsCode`}
              />
            </Grid.Col>
          )}
        </>
      )}
      {withAnticipatedCareLevel && (
        <Grid.Col>
          <Divider mt="var(--space-12)" mb="var(--space-20)" />
          <Controller
            control={control}
            name={`dependents.${index}.anticipatedCareLevel`}
            render={({ field }) => (
              <AnticipatedCareLevelInput
                {...field}
                isRequired={isFinalizationMode}
              />
            )}
          />
          <FormErrorMessage
            errors={formState.errors}
            fieldName={`dependents.${index}.anticipatedCareLevel`}
          />
        </Grid.Col>
      )}
    </Grid>
  );
};

type DependentsFormProps = {
  dependentsForm: UseFormReturn<InsuredDependentsFormFields>;
  onAppendDependent: () => void;
  targetEnrollmentDate: DateUtilInstance;
  isFinalizationMode: boolean;
  isEmployeeMode: boolean;
  withAnticipatedCareLevel?: boolean;
};

export const DependentsFormInputs = ({
  dependentsForm,
  isEmployeeMode,
  isFinalizationMode,
  onAppendDependent,
  targetEnrollmentDate,
  withAnticipatedCareLevel,
}: DependentsFormProps) => {
  'use no memo';
  const dependents = dependentsForm.watch('dependents');

  return (
    <>
      {dependents.map((_, index) => (
        // eslint-disable-next-line @eslint-react/no-array-index-key
        <Fragment key={index}>
          <DependentForm
            targetEnrollmentDate={targetEnrollmentDate}
            isFinalizationMode={isFinalizationMode}
            isEmployeeMode={isEmployeeMode}
            dependentsForm={dependentsForm}
            index={index}
            possibleFipsCodes={[]}
            setPossibleFipsCodes={() => {}}
            withAnticipatedCareLevel={withAnticipatedCareLevel}
          />
        </Fragment>
      ))}
      <Box
        w="100%"
        mx="auto"
        opacity={1}
        tabIndex={0}
        p="18px 48px"
        pos="relative"
        bg="zorroWhite.0"
        style={(theme) => ({
          cursor: 'pointer',
          borderRadius: theme.radius.md,
          boxShadow: `inset 0 0 0 1px ${theme.colors.zorroGray[7]}`,
        })}
        onClick={onAppendDependent}
        onKeyDown={(event) => {
          if (event.code === 'Space' || event.code === 'Enter') {
            event.preventDefault();
            onAppendDependent();
          }
        }}
      >
        <Text ta="center" c="zorroIris.9">
          + Add dependent
        </Text>
      </Box>
    </>
  );
};
